import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store';
import { selectDbData } from '../../../store/selectors/databaseSelectors';
import { addBatteryBrand, addBattery } from '../../../store/slices/databaseSlice';
import { batteryBrandsService, batteriesService } from '../../../services';
import { BatteryBrand, Battery } from '../../../types';
import { TOAST_CONFIG, TOAST_WARNING_CONFIG } from '../../../constants';
import { SearchDropdown } from '../../../components';
import toast from 'react-hot-toast';

interface BatteryBrandForm {
  name: string;
}

interface BatteryForm {
  batteryBrandId: string;
  batteryName: string;
  batteryModel: string;
  kwh: string;
  current: string;
  wattage: string;
}

export const BatteryManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const dbData = useAppSelector(selectDbData);
  
  // Form states
  const [brandForm, setBrandForm] = useState<BatteryBrandForm>({ name: '' });
  const [batteryForm, setBatteryForm] = useState<BatteryForm>({
    batteryBrandId: '',
    batteryName: '',
    batteryModel: '',
    kwh: '',
    current: '',
    wattage: ''
  });
  
  // UI states
  const [activeTab, setActiveTab] = useState<'brands' | 'batteries'>('brands');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localBrands, setLocalBrands] = useState<BatteryBrand[]>([]);
  const [localBatteries, setLocalBatteries] = useState<Battery[]>([]);
  const [selectedBrandOption, setSelectedBrandOption] = useState<{id: string, label: string} | null>(null);

  // Initialize local state with Redux data
  useEffect(() => {
    setLocalBrands(dbData.battery_brands);
    setLocalBatteries(dbData.batteries);
  }, [dbData.battery_brands, dbData.batteries]);

  // Create brand options for dropdown
  const brandOptions = localBrands.map(brand => ({
    id: brand.id,
    label: brand.name
  }));

  // Handle brand selection
  const handleBrandSelect = (option: {id: string, label: string} | null) => {
    setSelectedBrandOption(option);
    setBatteryForm(prev => ({ 
      ...prev, 
      batteryBrandId: option?.id || '' 
    }));
  };

  const handleBrandSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!brandForm.name.trim()) {
      toast('Please enter a brand name', TOAST_WARNING_CONFIG);
      return;
    }

    setIsSubmitting(true);
    try {
      const newBrand = await batteryBrandsService.createBrand({
        name: brandForm.name.trim()
      });
      
      // Update Redux state
      dispatch(addBatteryBrand(newBrand));
      
      // Update local state
      setLocalBrands(prev => [...prev, newBrand]);
      
      // Reset form
      setBrandForm({ name: '' });
      
      toast.success('Battery brand created successfully!', TOAST_CONFIG.toastOptions.success);
    } catch (error) {
      console.error('Error creating battery brand:', error);
      toast.error('Failed to create battery brand. Please try again.', TOAST_CONFIG.toastOptions.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBatterySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!batteryForm.batteryBrandId || !batteryForm.batteryModel.trim() || !batteryForm.batteryName.trim()) {
      toast('Please fill in all required fields.', TOAST_WARNING_CONFIG);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: any = {
        batteryName: batteryForm.batteryName.trim(),
        batteryModel: batteryForm.batteryModel.trim(),
        batteryBrandId: batteryForm.batteryBrandId
      };

      // Add kwh, current, and wattage if provided
      if (batteryForm.kwh.trim()) {
        payload.kwh = parseFloat(batteryForm.kwh);
      }
      if (batteryForm.current.trim()) {
        payload.current = parseFloat(batteryForm.current);
      }
      if (batteryForm.wattage.trim()) {
        payload.wattage = parseInt(batteryForm.wattage);
      }

      const newBattery = await batteriesService.createBattery(payload);
      
      // Find the selected brand and attach it to the new battery
      const selectedBrand = localBrands.find(brand => brand.id === batteryForm.batteryBrandId);
      
      if (selectedBrand) {
        const batteryWithBrand: Battery = {
          ...newBattery,
          batteryBrand: selectedBrand
        };
        
        // Update Redux state
        dispatch(addBattery(batteryWithBrand));
        
        // Update local state - add new battery at the beginning
        setLocalBatteries(prev => [batteryWithBrand, ...prev]);
      } else {
        // Fallback: use the battery as-is if brand not found
        dispatch(addBattery(newBattery));
        setLocalBatteries(prev => [newBattery, ...prev]);
      }
      
      // Reset form
      setBatteryForm({
        batteryBrandId: '',
        batteryName: '',
        batteryModel: '',
        kwh: '',
        current: '',
        wattage: ''
      });
      setSelectedBrandOption(null);
      
      toast.success('Battery created successfully!', TOAST_CONFIG.toastOptions.success);
    } catch (error) {
      console.error('Error creating battery:', error);
      toast.error('Failed to create battery. Please try again.', TOAST_CONFIG.toastOptions.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-4">

      {/* Tabs */}
      <div className="flex border-b border-slate-200 mb-4">
        <button
          onClick={() => setActiveTab('brands')}
          className={`px-3 py-2 font-medium text-sm border-b-2 transition-colors duration-200 ${
            activeTab === 'brands'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Brands ({localBrands.length})
        </button>
        <button
          onClick={() => setActiveTab('batteries')}
          className={`px-3 py-2 font-medium text-sm border-b-2 transition-colors duration-200 ${
            activeTab === 'batteries'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Batteries ({localBatteries.length})
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Form Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-3">
            {activeTab === 'brands' ? 'Add New Brand' : 'Add New Battery'}
          </h3>

          {activeTab === 'brands' ? (
            <form onSubmit={handleBrandSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Brand Name *
                </label>
                <input
                  type="text"
                  value={brandForm.name}
                  onChange={(e) => setBrandForm({ name: e.target.value })}
                  placeholder="e.g., TESLA"
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting || !brandForm.name.trim()}
                className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {isSubmitting ? 'Creating...' : 'Create Brand'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleBatterySubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Brand *
                </label>
                <SearchDropdown
                  options={brandOptions}
                  onSelect={handleBrandSelect}
                  placeholder="Select brand..."
                  value={selectedBrandOption}
                  className="text-sm"
                  inputClassName="px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  zIndex={1000}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Battery Name *
                </label>
                <input
                  type="text"
                  value={batteryForm.batteryName}
                  onChange={(e) => setBatteryForm(prev => ({ ...prev, batteryName: e.target.value }))}
                  placeholder="e.g., Powerwall"
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Model *
                </label>
                <input
                  type="text"
                  value={batteryForm.batteryModel}
                  onChange={(e) => setBatteryForm(prev => ({ ...prev, batteryModel: e.target.value }))}
                  placeholder="e.g., Model 2"
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    kWh
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={batteryForm.kwh}
                    onChange={(e) => setBatteryForm(prev => ({ ...prev, kwh: e.target.value }))}
                    placeholder="e.g., 13.5"
                    className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Current (A)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={batteryForm.current}
                    onChange={(e) => setBatteryForm(prev => ({ ...prev, current: e.target.value }))}
                    placeholder="e.g., 30.0"
                    className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Wattage (W)
                  </label>
                  <input
                    type="number"
                    value={batteryForm.wattage}
                    onChange={(e) => setBatteryForm(prev => ({ ...prev, wattage: e.target.value }))}
                    placeholder="e.g., 270"
                    className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                  />
                </div>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting || !batteryForm.batteryBrandId || !batteryForm.batteryModel.trim() || !batteryForm.batteryName.trim()}
                className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {isSubmitting ? 'Creating...' : 'Create Battery'}
              </button>
            </form>
          )}
        </div>

        {/* List Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-900">
              {activeTab === 'brands' ? 'Existing Brands' : 'Existing Batteries'}
            </h3>
            <span className="text-xs text-slate-500">
              {activeTab === 'brands' ? localBrands.length : localBatteries.length} items
            </span>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {activeTab === 'brands' ? (
              localBrands.length > 0 ? (
                <div className="flex flex-wrap gap-2 p-2">
                  {localBrands.map((brand) => (
                    <div key={brand.id} className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-50 text-emerald-800 rounded-full border border-emerald-200 hover:bg-emerald-100 transition-colors">
                      <span className="text-xs font-medium">{brand.name}</span>
                      <span className="text-xs bg-emerald-200 text-emerald-900 px-2 py-0.5 rounded-full">
                        {localBatteries.filter(b => b.batteryBrand?.id === brand.id).length}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-xs text-center py-8">No brands found</p>
              )
            ) : (
              localBatteries.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-1.5 px-2 font-medium text-slate-700">Brand</th>
                        <th className="text-left py-1.5 px-2 font-medium text-slate-700">Battery Name</th>
                        <th className="text-left py-1.5 px-2 font-medium text-slate-700">Model</th>
                        <th className="text-left py-1.5 px-2 font-medium text-slate-700">kWh</th>
                        <th className="text-left py-1.5 px-2 font-medium text-slate-700">Current (A)</th>
                        <th className="text-left py-1.5 px-2 font-medium text-slate-700">Wattage (W)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {localBatteries.map((battery) => (
                        <tr key={battery.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-1.5 px-2 text-slate-800">{battery.batteryBrand?.name || 'Unknown'}</td>
                          <td className="py-1.5 px-2 text-slate-800 font-medium">{battery.batteryName}</td>
                          <td className="py-1.5 px-2 text-slate-800">{battery.batteryModel}</td>
                          <td className="py-1.5 px-2 text-slate-800">
                            {battery.kwh ? battery.kwh : <span className="text-slate-400 italic">-</span>}
                          </td>
                          <td className="py-1.5 px-2 text-slate-800">
                            {battery.current ? battery.current : <span className="text-slate-400 italic">-</span>}
                          </td>
                          <td className="py-1.5 px-2 text-slate-800">
                            {battery.wattage ? battery.wattage : <span className="text-slate-400 italic">-</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-slate-500 text-xs text-center py-8">No batteries found</p>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};